/**
 * Aspect — PlacementTool (catalog drag-to-place)
 * --------------------------------------------------------------
 * Pick an asset from the catalog, preview it under the cursor on the floor,
 * then left-click in the 3D view to commit. Esc / right-click cancels.
 */

import * as pc from "playcanvas";
import { applyPlacementTransform, normalizeYaw } from "../scene/transforms.js";
import { QERotationInput } from "./rotation_keys.js";
import {
  resolveFurnitureXZ,
  isWallMounted,
  wallMountPoseFromRay,
} from "../scene/furniture_placement.js";
import {
  clampToSupportXZ,
  findSupportAt,
  isSurfaceItem,
  supportTopY,
} from "../scene/surface_stack.js";

const WALL_PADDING = 0.08;
const WALL_HALF_THICK = 0.075;
const DOOR_SLACK = 0.25;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function extentsXZ(fp, rotDeg, scale) {
  const r = (rotDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const s = Math.abs(Math.sin(r));
  const hw = (fp.w / 2) * scale;
  const hd = (fp.d / 2) * scale;
  return { ex: c * hw + s * hd, ez: s * hw + c * hd };
}

function closestOnSegment(ax, az, bx, bz, px, pz) {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  if (len2 < 1e-9) return { x: ax, z: az };
  const t = clamp(((px - ax) * abx + (pz - az) * abz) / len2, 0, 1);
  return { x: ax + t * abx, z: az + t * abz };
}

export class PlacementTool {
  constructor(app, camera, sceneManager, net, picker) {
    this.app = app;
    this.camera = camera;
    this.scene = sceneManager;
    this.net = net;
    this.picker = picker;
    this.canvas = app.graphicsDevice?.canvas ?? null;

    this._assetId = null;
    this._meta = null;
    this._ghost = null;
    this._ghostMat = null;
    this._x = 0;
    this._z = 0;
    this._placeY = 0;
    this._parentId = null;
    this._rotY = 0;
    this._floorY = 0;
    this._lastMouseX = 0;
    this._lastMouseY = 0;
    this._placing = false;
    this._committing = false;
    this._planMode = false;
    /** Bumped on cancel / new preview so late async loads never attach a ghost. */
    this._planPreviewGen = 0;
    this._wallSegCache = null;
    this._wallSegRoom = null;

    this._qeRotate = new QERotationInput({
      getYaw: () => this._rotY,
      setYaw: (y) => {
        this._rotY = y;
      },
      onChange: () => this._syncGhost(),
    });

    this.app.on("ui:place-asset", ({ asset_id }) => {
      if (document.body.classList.contains("plan-editor-open")) return;
      this.start(asset_id);
    });
    this.app.on("ui:cancel-placement", () => this.cancel());
    this.app.on("update", (dt) => this._update(dt));

    const m = app.mouse;
    if (m) {
      m.on(pc.EVENT_MOUSEMOVE, (e) => {
        this._lastMouseX = e.x;
        this._lastMouseY = e.y;
      });
      m.on(pc.EVENT_MOUSEDOWN, (e) => {
        if (!this._placing || this._planMode || this._committing || e.button !== pc.MOUSEBUTTON_LEFT) return;
        if (this._overUi(e)) return;
        e.event?.preventDefault?.();
        this._commit();
      });
    }
    if (app.touch) {
      app.touch.on(pc.EVENT_TOUCHMOVE, (e) => {
        const t = e.touches?.[0];
        if (!t || !this._placing || this._planMode) return;
        this._lastMouseX = t.x;
        this._lastMouseY = t.y;
      });
      app.touch.on(pc.EVENT_TOUCHSTART, (e) => {
        if (!this._placing || this._planMode || this._committing) return;
        const t = e.touches?.[0];
        if (!t || this._overUi(e)) return;
        this._lastMouseX = t.x;
        this._lastMouseY = t.y;
        this._syncGhost();
        e.event?.preventDefault?.();
        this._commit();
      });
    }
    app.keyboard?.on(pc.EVENT_KEYDOWN, (e) => {
      if (e.key === pc.KEY_ESCAPE && this._placing && !this._planMode) this.cancel();
      if (!this._placing || this._planMode) return;
      if (e.key === pc.KEY_Q) this._qeRotate.onKeyDown("q", e.event?.repeat);
      else if (e.key === pc.KEY_E) this._qeRotate.onKeyDown("e", e.event?.repeat);
    });
    app.keyboard?.on(pc.EVENT_KEYUP, (e) => {
      if (!this._placing || this._planMode) return;
      if (e.key === pc.KEY_Q) this._qeRotate.onKeyUp("q");
      else if (e.key === pc.KEY_E) this._qeRotate.onKeyUp("e");
    });

    this.app.on("scene:room-loaded", () => {
      if (this._placing) this.cancel();
    });
  }

  get active() {
    return this._placing;
  }

  _overUi(e) {
    const t = e.event?.target;
    if (!t) return false;
    // PlayCanvas mouse events fire on the canvas, which sits next to #overlay.
    // Never treat the 3D view itself as chrome — that used to swallow every place-click.
    if (t === this.canvas || t.id === "app-canvas" || t.tagName === "CANVAS") return false;
    return Boolean(
      t.closest?.(
        "button, a, input, textarea, select, [data-overlay='catalog-panel'], [data-overlay='touch-plan-bar'], .catalog-panel, .prompt-bar, .hud-btn, .plan-editor, .buy-soon",
      ),
    );
  }

  async start(asset_id) {
    if (!asset_id) return;
    const meta = this.scene.catalog.get(asset_id);
    if (!meta) {
      this.app.fire("ui:placement-hint", { text: "Unknown catalog item.", error: true });
      return;
    }
    if (!this.scene.room) {
      this.app.fire("ui:placement-hint", { text: "Load a room before placing furniture.", error: true });
      return;
    }

    this.cancel(false);
    this.picker.deselect();

    this._assetId = asset_id;
    this._meta = meta;
    this._floorY = this.scene.room.floorY ?? 0;
    this._rotY = 0;
    this._placing = true;
    this._planMode = false;

    try {
      const glb = meta.glb ?? `/assets/models/${asset_id}.glb`;
      const entity = await this.scene.registry.create(asset_id, glb, meta.footprint, meta.category);
      if (!this._placing || this._planMode || this._assetId !== asset_id) {
        entity.destroy();
        return;
      }
      this._attachGhost(entity);
      this._aimFromCamera();
      this._syncGhost();
    } catch (err) {
      console.warn("[placement] preview failed", err);
      this.cancel(false);
      this.app.fire("ui:placement-hint", {
        text: err?.message ?? "Could not load that item — try another.",
        error: true,
      });
    }
    this.app.fire("ui:placement-started", { asset_id, name: meta.name });
    this.app.fire("ui:placement-hint", {
      text: `Placing ${meta.name} — click once in the room to place · Q/E rotate · Esc cancel`,
    });
  }

  /**
   * Semi-transparent 3D preview driven by the 2D plan editor (not the mouse ray).
   * Position updates come from PlanEditor._sync3dPlacementGhost().
   */
  async startPlanPreview(asset_id) {
    if (!asset_id) return false;
    const meta = this.scene.catalog.get(asset_id);
    if (!meta || !this.scene.room) return false;

    this.cancel(false);
    this.picker.deselect();

    const gen = ++this._planPreviewGen;
    this._assetId = asset_id;
    this._meta = meta;
    this._floorY = this.scene.room.floorY ?? 0;
    this._rotY = 0;
    this._placing = true;
    this._planMode = true;
    this._wallSegRoom = null;
    this._wallSegCache = null;

    try {
      const glb = meta.glb ?? `/assets/models/${asset_id}.glb`;
      const entity = await this.scene.registry.create(asset_id, glb, meta.footprint, meta.category);
      if (gen !== this._planPreviewGen || !this._planMode || this._assetId !== asset_id) {
        entity.destroy();
        return false;
      }
      this._attachGhost(entity);
      this._ghost.enabled = false;
      return true;
    } catch (err) {
      console.warn("[placement] plan preview failed", err);
      if (gen === this._planPreviewGen) this.cancel(false);
      return false;
    }
  }

  /** Move the plan-editor 3D ghost to a world pose (called on every 2D pointer move). */
  updatePlanPreview(x, z, rotY) {
    if (!this._planMode || !this._ghost || !this._meta) return;
    this._ghost.enabled = true;
    this._x = x;
    this._z = z;
    this._rotY = normalizeYaw(rotY ?? 0);
    applyPlacementTransform(this._ghost, {
      position: [this._x, this._floorY, this._z],
      rotation_y: this._rotY,
      scale: 1,
      footprint: this._meta.footprint ?? { w: 1, d: 1 },
    });
  }

  _attachGhost(entity) {
    this._ghostMat = new pc.StandardMaterial();
    this._ghostMat.diffuse = new pc.Color(0.5, 0.85, 1);
    this._ghostMat.emissive = new pc.Color(0.2, 0.5, 0.9);
    this._ghostMat.emissiveIntensity = 0.35;
    this._ghostMat.opacity = 0.55;
    this._ghostMat.blendType = pc.BLEND_NORMAL;
    this._ghostMat.depthWrite = false;
    this._ghostMat.useMetalness = true;
    this._ghostMat.metalness = 0;
    this._ghostMat.gloss = 0.4;
    this._ghostMat.update();
    for (const r of entity.findComponents("render")) r.material = this._ghostMat;
    this._ghost = entity;
    this._ghost.name = "placement-ghost";
    this.app.root.addChild(this._ghost);
  }

  cancel(fireEvent = true) {
    this._planPreviewGen += 1;
    this._qeRotate.reset();
    if (this._ghost) {
      this._ghost.destroy();
      this._ghost = null;
    }
    if (this._ghostMat) {
      this._ghostMat.destroy();
      this._ghostMat = null;
    }
    const was = this._placing;
    this._assetId = null;
    this._meta = null;
    this._placing = false;
    this._committing = false;
    this._planMode = false;
    if (was && fireEvent) this.app.fire("ui:placement-ended", {});
  }

  _pointerLocked() {
    return this.canvas && document.pointerLockElement === this.canvas;
  }

  _cursorScreenCoords() {
    if (this._pointerLocked()) {
      return { x: this.canvas.clientWidth / 2, y: this.canvas.clientHeight / 2 };
    }
    return { x: this._lastMouseX, y: this._lastMouseY };
  }

  _pointerOnFloor(x, y) {
    const cam = this.camera.camera;
    const near = cam.screenToWorld(x, y, cam.nearClip);
    const far = cam.screenToWorld(x, y, cam.farClip);
    const dir = far.clone().sub(near).normalize();
    if (Math.abs(dir.y) < 1e-6) return null;
    const t = (this._floorY - near.y) / dir.y;
    if (t < 0) return null;
    return new pc.Vec3(near.x + dir.x * t, this._floorY, near.z + dir.z * t);
  }

  /** Floor point the player is aiming at — screen ray, then camera-forward fallback. */
  _aimOnFloor() {
    const { x, y } = this._cursorScreenCoords();
    const hit = this._pointerOnFloor(x, y);
    if (hit) return hit;

    const pos = this.camera.getPosition();
    const fwd = this.camera.forward;
    const flat = Math.hypot(fwd.x, fwd.z);
    if (Math.abs(fwd.y) > 1e-4) {
      const t = (this._floorY - pos.y) / fwd.y;
      if (t > 0.35 && t < 40) {
        return new pc.Vec3(pos.x + fwd.x * t, this._floorY, pos.z + fwd.z * t);
      }
    }
    const nx = flat > 1e-4 ? fwd.x / flat : 0;
    const nz = flat > 1e-4 ? fwd.z / flat : 1;
    return new pc.Vec3(pos.x + nx * 2.6, this._floorY, pos.z + nz * 2.6);
  }

  _aimFromCamera() {
    const hit = this._aimOnFloor();
    if (!hit) return;
    this._x = hit.x;
    this._z = hit.z;
    this._placeY = this._floorY;
  }

  _wallSegments() {
    const room = this.scene.room;
    if (!room?.walls) return [];
    if (this._wallSegRoom === room && this._wallSegCache) return this._wallSegCache;
    const openings = room.openings ?? [];
    const segs = [];
    for (const w of room.walls) {
      const ax = w.start[0];
      const az = w.start[1];
      const bx = w.end[0];
      const bz = w.end[1];
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 1e-4) continue;
      const half = (w.thickness ?? WALL_HALF_THICK * 2) / 2;
      const gaps = [];
      for (const op of openings) {
        if (op.wall !== w.id || op.type !== "door") continue;
        const tc = ((op.center[0] - ax) * (bx - ax) + (op.center[2] - az) * (bz - az)) / (len * len);
        const hw = ((op.width ?? 0.9) / 2 + DOOR_SLACK) / len;
        gaps.push([clamp(tc - hw, 0, 1), clamp(tc + hw, 0, 1)]);
      }
      gaps.sort((a, b) => a[0] - b[0]);
      const dirx = (bx - ax) / len;
      const dirz = (bz - az) / len;
      const add = (t0, t1) => {
        if (t1 - t0 < 0.02) return;
        segs.push({
          ax: ax + dirx * len * t0,
          az: az + dirz * len * t0,
          bx: ax + dirx * len * t1,
          bz: az + dirz * len * t1,
          half,
          nx: -dirz,
          nz: dirx,
        });
      };
      let cursor = 0;
      for (const [g0, g1] of gaps) {
        if (g0 > cursor) add(cursor, g0);
        cursor = Math.max(cursor, g1);
      }
      if (cursor < 1) add(cursor, 1);
    }
    this._wallSegRoom = room;
    this._wallSegCache = segs;
    return segs;
  }

  _resolveAgainstWalls(px, pz) {
    const segs = this._wallSegments();
    const fp = this._meta?.footprint ?? { w: 1, d: 1 };
    const { ex, ez } = extentsXZ(fp, this._rotY, 1);
    let x = px;
    let z = pz;
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const s of segs) {
        const cp = closestOnSegment(s.ax, s.az, s.bx, s.bz, x, z);
        let dx = x - cp.x;
        let dz = z - cp.z;
        let dist = Math.hypot(dx, dz);
        let nx;
        let nz;
        if (dist < 1e-6) {
          nx = s.nx;
          nz = s.nz;
          dist = 0;
        } else {
          nx = dx / dist;
          nz = dz / dist;
        }
        const reach = s.half + (ex * Math.abs(nx) + ez * Math.abs(nz)) + WALL_PADDING;
        if (dist < reach - 1e-4) {
          x = cp.x + nx * reach;
          z = cp.z + nz * reach;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return { x, z };
  }

  _syncGhost() {
    if (!this._ghost || !this._meta) return;
    const { x, y } = this._cursorScreenCoords();

    // Wall-hung items preview flush on the wall face under the cursor
    // (mesh-exact, sliding in X/Z and height, auto-facing the room).
    if (isWallMounted(this._meta)) {
      const cam = this.camera.camera;
      const near = cam.screenToWorld(x, y, cam.nearClip);
      const far = cam.screenToWorld(x, y, cam.farClip);
      const dir = far.clone().sub(near).normalize();
      const pose = wallMountPoseFromRay(near, dir, this.scene.room, this._meta.footprint, 1, this._meta.id ?? this._meta.asset_id);
      if (!pose) return; // no wall under the cursor — hold the last preview pose
      this._x = pose.x;
      this._z = pose.z;
      this._placeY = pose.y;
      this._rotY = pose.rotation_y;
      this._parentId = null;
      applyPlacementTransform(this._ghost, {
        position: [this._x, this._placeY, this._z],
        rotation_y: this._rotY,
        scale: 1,
        footprint: this._meta.footprint ?? { w: 1, d: 1 },
      });
      return;
    }

    const hit = this._aimOnFloor();
    if (!hit) return;

    const fp = this._meta.footprint ?? { w: 1, d: 1 };
    const placements = [...(this.scene.placements?.values?.() ?? [])];
    const catalogLookup = (assetId) => this.scene.catalog?.get?.(assetId);
    let wx = hit.x;
    let wz = hit.z;
    let support = null;
    const surfaceCapable = isSurfaceItem(this._meta);

    if (surfaceCapable) {
      support = findSupportAt(wx, wz, placements, { catalogLookup });
      if (support) {
        const clamped = clampToSupportXZ(wx, wz, fp, this._rotY, 1, support);
        wx = clamped.x;
        wz = clamped.z;
      }
    }

    // Aimed floor point only — do not shove off rugs / nearby furniture, and
    // do not keep a sticky previous pose (that left the ghost beside the cursor).
    const resolved = resolveFurnitureXZ(
      wx,
      wz,
      {
        footprint: fp,
        rotation_y: this._rotY,
        scale: 1,
        excludeId: support ? new Set([support.id]) : null,
        prev: null,
      },
      this.scene.room,
      null,
    );
    this._x = resolved.x;
    this._z = resolved.z;

    if (surfaceCapable) {
      support = findSupportAt(this._x, this._z, placements, { catalogLookup });
      if (support) {
        const clamped = clampToSupportXZ(this._x, this._z, fp, this._rotY, 1, support);
        this._x = clamped.x;
        this._z = clamped.z;
        this._placeY = supportTopY(support);
        this._parentId = support.id;
      } else {
        this._placeY = this._floorY;
        this._parentId = null;
      }
    } else {
      this._placeY = this._floorY;
      this._parentId = null;
    }

    applyPlacementTransform(this._ghost, {
      position: [this._x, this._placeY, this._z],
      rotation_y: this._rotY,
      scale: 1,
      footprint: fp,
    });
  }

  _update(dt) {
    if (!this._placing || this._planMode) return;
    if (this._qeRotate.needsTick()) this._qeRotate.tick(dt);
    this._syncGhost();
  }

  async _commit() {
    if (!this._placing || !this._assetId || this._committing) return;
    this._committing = true;

    const asset_id = this._assetId;
    const meta = this._meta;
    const fp = meta?.footprint ?? { w: 1, d: 1 };
    const rotation_y = normalizeYaw(this._rotY);
    const parent_id = this._parentId;
    const placeY = this._placeY ?? this._floorY;

    const position = [this._x, placeY, this._z];

    // One catalog pick = one placement; end ghost mode before the network round-trip.
    this.cancel(false);
    this.app.fire("ui:placement-ended", { asset_id });

    try {
      if (this.scene.sandbox) {
        const id = `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
        await this.scene.onSpawn({
          id,
          asset_id,
          position,
          rotation_y,
          scale: 1,
          parent_id: parent_id || null,
          glb: meta?.glb,
          footprint: fp,
          category: meta?.category,
          price: meta?.price,
        });
        this.app.fire("ui:placement-hint", {
          text: `${meta?.name ?? "Item"} placed — pick from the catalog to place another.`,
        });
        return;
      }
      const result = await this.net.request(
        "viewport:place_furniture",
        { asset_id, position, rotation_y, scale: 1, parent_id: parent_id || null, exact: true },
        12_000,
      );
      // Don't rely only on the furniture:spawn echo — if that frame is dropped
      // the item exists on the server but never appears in the viewport.
      if (result?.placement) await this.scene.onSpawn(result.placement);
      this.app.fire("ui:placement-hint", {
        text: `${meta?.name ?? "Item"} placed — pick from the catalog to place another.`,
      });
    } catch (err) {
      this.app.fire("ui:placement-hint", {
        text: err?.message ?? err?.data?.reason ?? "Could not place here — pick the item again to retry.",
        error: true,
      });
    } finally {
      this._committing = false;
    }
  }

  destroy() {
    this.cancel(false);
  }
}

export default PlacementTool;
